import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { X, Radar, Brain, FileText, Bot, KanbanSquare, ArrowRight, ArrowLeft, ShieldCheck, Sparkles } from "lucide-react";

const STEPS = [
  {
    icon: Sparkles,
    eyebrow: "WELCOME OPERATOR",
    title: "BidForge is your AI business development team.",
    body: "Every agent in the squad specializes in a different stage of the government contracting lifecycle — from discovery to award. Here's the 60-second tour.",
    cta: null,
  },
  {
    icon: Radar,
    eyebrow: "AGENT 01",
    title: "Discovery Agent",
    body: "Continuously scans SAM.gov (federal, state, local) and ranks every new solicitation against your NAICS, PSC, bonding, and service area. Open the Opportunities feed to see today's matches.",
    cta: { to: "/opportunities", label: "Show Opportunities" },
  },
  {
    icon: Brain,
    eyebrow: "AGENT 02",
    title: "Bid/No-Bid Agent",
    body: "Click any opportunity and hit ANALYZE. In minutes you'll get an executive briefing with win probability, risks, and a go/no-go rationale — before you touch the solicitation.",
    cta: null,
  },
  {
    icon: FileText,
    eyebrow: "AGENT 03",
    title: "Proposal + Estimating Agents",
    body: "GENERATE PROPOSAL drafts a 14-section, agency-ready proposal in your voice. OPEN ESTIMATING AGENT turns the RFP scope into a work-breakdown structure — drop in your pricing and totals roll up automatically.",
    cta: null,
  },
  {
    icon: KanbanSquare,
    eyebrow: "AGENT 04",
    title: "Pipeline + Amendment Agents",
    body: "Every bid moves through the Pipeline board. The Amendment Monitor diffs solicitation changes the day they publish so your proposal never chases yesterday's requirements.",
    cta: { to: "/pipeline", label: "Open Pipeline" },
  },
  {
    icon: Bot,
    eyebrow: "AGENT 05",
    title: "Autonomy Agent",
    body: "Configure 0–10 auto-drafted proposals per day (Business tier). The squad wakes up, scores fit, drafts, and hands you a review queue by morning.",
    cta: null,
  },
  {
    icon: ShieldCheck,
    eyebrow: "ONE MORE THING",
    title: "You're on a 7-day Starter trial.",
    body: "Explore everything. When you're ready to unlock the Proposal, Bid/No-Bid, and Autonomy agents at full power, upgrade in the Billing page.",
    cta: { to: "/pricing", label: "See plans" },
  },
];

export default function OnboardingTour({ user }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    // Skip the tour for super_admin or non-owner accounts, or if the user has already seen it.
    if (user.super_admin || user.role !== "owner") return;
    api.get("/onboarding/status").then(r => {
      if (!r.data.onboarded) setOpen(true);
    }).catch(() => {});
  }, [user]);

  const finish = async () => {
    setOpen(false);
    try { await api.post("/onboarding/complete"); } catch {}
  };

  if (!open) return null;
  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-modal">
      <div className="bg-[#0D1424] border border-cyan-400/40 max-w-2xl w-full cyan-glow relative">
        <button onClick={finish} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="onboarding-close" aria-label="Skip tour"><X className="w-5 h-5" /></button>
        <div className="p-10">
          <div className="mono-label text-cyan-400 mb-4 flex items-center gap-2">
            <Icon className="w-4 h-4" />{s.eyebrow}
          </div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-4" data-testid="onboarding-title">{s.title}</h2>
          <p className="text-slate-300 text-lg leading-relaxed mb-6">{s.body}</p>

          <div className="flex items-center gap-1.5 mb-8" data-testid="onboarding-progress">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1 flex-1 transition-colors ${i <= step ? "bg-cyan-400" : "bg-white/10"}`} />
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="mono-label text-slate-400 hover:text-white flex items-center gap-2 disabled:opacity-30" data-testid="onboarding-back">
              <ArrowLeft className="w-3.5 h-3.5" />BACK
            </button>
            <div className="flex items-center gap-3">
              {s.cta && (
                <Link to={s.cta.to} onClick={finish} className="mono-label border border-white/20 hover:border-cyan-400 hover:text-cyan-400 px-4 py-2" data-testid="onboarding-cta">
                  {s.cta.label}
                </Link>
              )}
              {!isLast ? (
                <button onClick={() => setStep(step + 1)} className="mono-label bg-cyan-400 text-[#070B14] hover:bg-cyan-300 px-5 py-2 flex items-center gap-2" data-testid="onboarding-next">
                  NEXT <ArrowRight className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button onClick={finish} className="mono-label bg-cyan-400 text-[#070B14] hover:bg-cyan-300 px-5 py-2 flex items-center gap-2" data-testid="onboarding-finish">
                  START USING BIDFORGE <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
